import { useCallback, useEffect, useRef, useState } from 'react'
import { Extension } from '@tiptap/core'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Image from '@tiptap/extension-image'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import FontFamily from '@tiptap/extension-font-family'
import { TextStyle } from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Link as LinkIcon,
  Undo,
  Redo,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
  RemoveFormatting,
  Palette,
  Highlighter,
  ImageIcon,
  Loader2,
  Indent,
  Outdent,
  ChevronDown,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { t } from '@/shared/lib/i18n'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'

interface Props {
  id?: string
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  className?: string
  imageUploadUrl?: string
}

const FONT_FAMILIES = [
  { label: 'Sans Serif', value: 'Inter, sans-serif' },
  { label: 'Serif', value: 'Georgia, serif' },
  { label: 'Monospace', value: 'JetBrains Mono, monospace' },
]

const FONT_SIZES = [
  { label: 'Small', value: '0.875em' },
  { label: 'Normal', value: '' },
  { label: 'Large', value: '1.25em' },
  { label: 'Huge', value: '1.75em' },
]

const HEADING_OPTIONS = [
  { label: 'Normal', level: 0 },
  { label: 'Heading 1', level: 1 },
  { label: 'Heading 2', level: 2 },
  { label: 'Heading 3', level: 3 },
  { label: 'Heading 4', level: 4 },
  { label: 'Heading 5', level: 5 },
  { label: 'Heading 6', level: 6 },
] as const

const TEXT_COLORS = [
  '#000000',
  '#434343',
  '#666666',
  '#999999',
  '#b7b7b7',
  '#e53935',
  '#d81b60',
  '#8e24aa',
  '#5c6bc0',
  '#1e88e5',
  '#00acc1',
  '#00897b',
  '#43a047',
  '#7cb342',
  '#c0ca33',
  '#fdd835',
  '#ffb300',
  '#fb8c00',
  '#f4511e',
  '#6d4c41',
]

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (size: string) => ReturnType
      unsetFontSize: () => ReturnType
    }
    customIndent: {
      indent: () => ReturnType
      outdent: () => ReturnType
    }
  }
}

const FontSize = Extension.create({
  name: 'fontSize',

  addOptions() {
    return { types: ['textStyle'] }
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {}
              return { style: `font-size: ${attributes.fontSize}` }
            },
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      setFontSize:
        (size: string) =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize: size }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    }
  },
})

const INDENT_STEP = 24

const IndentExtension = Extension.create({
  name: 'customIndent',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading', 'bulletList', 'orderedList'],
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) => {
              const marginLeft = element.style.marginLeft
              return marginLeft ? parseInt(marginLeft, 10) / INDENT_STEP : 0
            },
            renderHTML: (attributes) => {
              if (!attributes.indent || attributes.indent <= 0) return {}
              return { style: `margin-left: ${attributes.indent * INDENT_STEP}px` }
            },
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      indent:
        () =>
        ({ tr, state, dispatch }) => {
          const { selection } = state
          const { from, to } = selection

          state.doc.nodesBetween(from, to, (node, pos) => {
            if (node.isBlock && node.attrs.indent !== undefined) {
              const newIndent = Math.min((node.attrs.indent || 0) + 1, 10)
              if (dispatch) {
                tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: newIndent })
              }
            }
          })

          return true
        },
      outdent:
        () =>
        ({ tr, state, dispatch }) => {
          const { selection } = state
          const { from, to } = selection

          state.doc.nodesBetween(from, to, (node, pos) => {
            if (node.isBlock && node.attrs.indent !== undefined) {
              const newIndent = Math.max((node.attrs.indent || 0) - 1, 0)
              if (dispatch) {
                tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: newIndent })
              }
            }
          })

          return true
        },
    }
  },
})

const ToolbarButton = ({
  active,
  onClick,
  children,
  title,
  disabled,
}: {
  active?: boolean
  onClick: () => void
  children: React.ReactNode
  title?: string
  disabled?: boolean
}) => (
  <span className="group/tooltip relative inline-flex">
    <button
      type="button"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'rounded p-1 transition-colors hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed',
        active && 'bg-muted text-primary',
      )}
    >
      {children}
    </button>
    {title && (
      <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-primary px-2 py-1 text-xs text-primary-foreground opacity-0 transition-opacity group-hover/tooltip:opacity-100">
        {title}
      </span>
    )}
  </span>
)

const ToolbarDropdown = ({
  label,
  children,
  title,
}: {
  label: string
  children: React.ReactNode
  title?: string
}) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        title={title}
        onMouseDown={(event) => event.preventDefault()}
        className="inline-flex items-center gap-0.5 rounded px-1.5 py-1 text-xs transition-colors hover:bg-muted"
      >
        {label}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-40 p-1" align="start">
      {children}
    </PopoverContent>
  </Popover>
)

const ColorPickerGrid = ({
  onSelect,
  onClear,
  activeColor,
}: {
  onSelect: (color: string) => void
  onClear?: () => void
  activeColor?: string
}) => (
  <div className="grid grid-cols-5 gap-1 p-1">
    {onClear && (
      <button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        onClick={onClear}
        title={t('shared.richTextEditor.noColor')}
        aria-label={t('shared.richTextEditor.noColor')}
        className={cn(
          'relative h-5 w-5 overflow-hidden rounded border border-border bg-background transition-transform hover:scale-110',
          !activeColor && 'ring-2 ring-primary ring-offset-1',
        )}
      >
        <span
          aria-hidden
          className="absolute left-1/2 top-1/2 h-px w-6 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-destructive"
        />
      </button>
    )}
    {TEXT_COLORS.map((color) => (
      <button
        key={color}
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => onSelect(color)}
        className={cn(
          'h-5 w-5 rounded border border-border transition-transform hover:scale-110',
          activeColor === color && 'ring-2 ring-primary ring-offset-1',
        )}
        style={{ backgroundColor: color }}
        title={color}
      />
    ))}
  </div>
)

const ABSOLUTE_URL_PROTOCOL_PATTERN = /^(https?:|mailto:|tel:|ftp:)/i

const normalizeExternalLinkUrl = (rawUrl: string): string => {
  const trimmedUrl = rawUrl.trim()
  if (!trimmedUrl) return trimmedUrl

  if (ABSOLUTE_URL_PROTOCOL_PATTERN.test(trimmedUrl)) {
    return trimmedUrl
  }

  if (trimmedUrl.startsWith('//')) {
    return `https:${trimmedUrl}`
  }

  if (trimmedUrl.startsWith('/') || trimmedUrl.startsWith('#') || trimmedUrl.startsWith('?')) {
    return trimmedUrl
  }

  return `https://${trimmedUrl}`
}

// A non-collapsed selection can still hold nothing to mark: after Ctrl+A + Delete on a cleared
// field ProseMirror keeps an AllSelection spanning the empty paragraph (`empty === false`,
// no text). Leaf nodes such as images count as content so they are never replaced.
const OBJECT_REPLACEMENT_CHAR = '\ufffc'

const selectionHasContent = (editor: ReturnType<typeof useEditor> & object): boolean => {
  const { selection, doc } = editor.state
  if (selection.empty) return false

  return doc.textBetween(selection.from, selection.to, '', OBJECT_REPLACEMENT_CHAR) !== ''
}

// REBUILD-630: `setLink` only marks existing content. With a collapsed cursor (or a selection
// holding no text) it merely arms a stored mark, so the saved URL never reached the Comments
// field. Selected content (or a cursor inside an existing link) is linked in place; otherwise
// the URL is inserted as linked text, ending with an unlinked space so typing continues
// outside the link.
const applyLinkToEditor = (editor: ReturnType<typeof useEditor> & object, href: string) => {
  const chain = editor.chain().focus()

  if (selectionHasContent(editor)) {
    chain.setLink({ href }).run()
    return
  }

  if (editor.isActive('link')) {
    chain.extendMarkRange('link').setLink({ href }).run()
    return
  }

  chain
    .insertContent([
      { type: 'text', text: href, marks: [{ type: 'link', attrs: { href } }] },
      { type: 'text', text: ' ' },
    ])
    .run()
}

const LinkPopover = ({
  editor,
  iconSize,
}: {
  editor: ReturnType<typeof useEditor> & object
  iconSize: string
}) => {
  const [linkUrl, setLinkUrl] = useState('')
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleOpen = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (nextOpen) {
      const existingHref = editor.getAttributes('link').href as string | undefined
      setLinkUrl(existingHref ?? '')
    }
  }

  const handleSave = () => {
    const trimmedUrl = linkUrl.trim()
    if (!trimmedUrl) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      setOpen(false)
      return
    }

    applyLinkToEditor(editor, normalizeExternalLinkUrl(trimmedUrl))
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <span className="group/tooltip relative inline-flex">
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            className={cn(
              'rounded p-1 transition-colors hover:bg-muted',
              editor.isActive('link') && 'bg-muted text-primary',
            )}
          >
            <LinkIcon className={iconSize} />
          </button>
          <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-primary px-2 py-1 text-xs text-primary-foreground opacity-0 transition-opacity group-hover/tooltip:opacity-100">
            Link
          </span>
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground whitespace-nowrap">Enter link:</span>
          <input
            ref={inputRef}
            type="url"
            value={linkUrl}
            onChange={(event) => setLinkUrl(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                handleSave()
              }
            }}
            placeholder="https://..."
            className="h-7 w-48 rounded border border-input px-2 text-sm outline-none focus:ring-1 focus:ring-ring"
          />
          <button
            type="button"
            onClick={handleSave}
            className="rounded px-2 py-1 text-xs font-medium transition-colors hover:bg-muted"
          >
            Save
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

async function uploadImage(file: File, uploadUrl: string): Promise<string> {
  const formData = new FormData()
  formData.append('file', file)

  const csrfMeta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
  const headers: Record<string, string> = {}
  if (csrfMeta) {
    headers['X-CSRF-Token'] = csrfMeta.content
  }

  const response = await fetch(uploadUrl, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'same-origin',
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(`Upload failed: ${response.status} ${errorBody}`)
  }

  const data = await response.json()
  return data.url
}

const DEFAULT_IMAGE_UPLOAD_URL = '/rich_text_images'

export const RichTextEditor = ({
  id,
  value,
  onChange,
  onBlur,
  placeholder,
  className,
  imageUploadUrl = DEFAULT_IMAGE_UPLOAD_URL,
}: Props) => {
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [, setToolbarRevision] = useState(0)

  const handleImageUpload = useCallback(
    async (file: File) => {
      setUploading(true)
      setUploadError(null)
      try {
        const url = await uploadImage(file, imageUploadUrl)
        return url
      } catch {
        setUploadError(t('shared.richTextEditor.uploadFailed'))
        throw new Error('upload_failed')
      } finally {
        setUploading(false)
      }
    },
    [imageUploadUrl],
  )

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        // StarterKit v3 bundles Link and Underline; they are registered explicitly below, so
        // keep the bundled copies off to avoid tiptap's "Duplicate extension names" warning.
        link: false,
        underline: false,
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      Image.configure({ inline: true, allowBase64: false }),
      Subscript,
      Superscript,
      TextStyle,
      FontFamily,
      FontSize,
      Color,
      Highlight.configure({ multicolor: true }),
      IndentExtension,
    ],
    content: value,
    onUpdate: ({ editor: editorInstance }) => {
      onChange(editorInstance.getHTML())
    },
    onSelectionUpdate: () => setToolbarRevision((revision) => revision + 1),
    onTransaction: () => setToolbarRevision((revision) => revision + 1),
    editorProps: {
      handlePaste: (_view, event) => {
        const clipboardItems = event.clipboardData?.items
        if (!clipboardItems) return false

        for (const item of clipboardItems) {
          if (item.type.startsWith('image/')) {
            event.preventDefault()
            const file = item.getAsFile()
            if (!file) return true

            handleImageUpload(file)
              .then((url) => {
                editor?.chain().focus().setImage({ src: url }).run()
              })
              .catch(() => {})
            return true
          }
        }

        return false
      },
      handleDrop: (_view, event) => {
        const files = event.dataTransfer?.files
        if (!files?.length) return false

        const imageFile = Array.from(files).find((file) => file.type.startsWith('image/'))
        if (!imageFile) return false

        event.preventDefault()
        handleImageUpload(imageFile)
          .then((url) => {
            editor?.chain().focus().setImage({ src: url }).run()
          })
          .catch(() => {})
        return true
      },
    },
  })

  useEffect(() => {
    if (!editor) return
    const currentContent = editor.getHTML()
    if (value !== currentContent) {
      editor.commands.setContent(value)
    }
  }, [value, editor])

  if (!editor) return null

  const currentFontSize = (editor.getAttributes('textStyle').fontSize as string) || ''
  const currentFontSizeLabel =
    FONT_SIZES.find((fs) => fs.value === currentFontSize)?.label ?? 'Normal'

  const currentHeadingLevel = (() => {
    for (let level = 1; level <= 6; level++) {
      if (editor.isActive('heading', { level })) return level
    }
    return 0
  })()
  const currentHeadingLabel =
    HEADING_OPTIONS.find((h) => h.level === currentHeadingLevel)?.label ?? 'Normal'

  const iconSize = 'h-4 w-4'

  return (
    <div
      id={id}
      className={cn('rounded-md border bg-background', className)}
      onBlurCapture={(event) => {
        // Fire onBlur only when focus truly leaves the editor (not when moving to a
        // toolbar button or popover inside it).
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
        onBlur?.()
      }}
    >
      <div className="flex flex-wrap items-center gap-x-0.5 gap-y-1.5 border-b px-2 py-1.5">
        {/* Text formatting */}
        <ToolbarButton
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        >
          <Bold className={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
        >
          <Italic className={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline"
        >
          <UnderlineIcon className={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          title="Strikethrough"
        >
          <Strikethrough className={iconSize} />
        </ToolbarButton>

        <span className="mx-1 h-4 w-px bg-border" />

        {/* Lists */}
        <ToolbarButton
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet List"
        >
          <List className={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Ordered List"
        >
          <ListOrdered className={iconSize} />
        </ToolbarButton>

        <span className="mx-1 h-4 w-px bg-border" />

        {/* Sub/Superscript */}
        <ToolbarButton
          active={editor.isActive('subscript')}
          onClick={() => editor.chain().focus().toggleSubscript().run()}
          title="Subscript"
        >
          <SubscriptIcon className={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('superscript')}
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
          title="Superscript"
        >
          <SuperscriptIcon className={iconSize} />
        </ToolbarButton>

        <span className="mx-1 h-4 w-px bg-border" />

        {/* Alignment */}
        <ToolbarButton
          active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          title="Align Left"
        >
          <AlignLeft className={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          title="Align Center"
        >
          <AlignCenter className={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          title="Align Right"
        >
          <AlignRight className={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: 'justify' })}
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          title="Justify"
        >
          <AlignJustify className={iconSize} />
        </ToolbarButton>

        <span className="mx-1 h-4 w-px bg-border" />

        {/* Indent / Outdent */}
        <ToolbarButton
          onClick={() => editor.chain().focus().outdent().run()}
          title="Decrease Indent"
        >
          <Outdent className={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().indent().run()}
          title="Increase Indent"
        >
          <Indent className={iconSize} />
        </ToolbarButton>

        <span className="mx-1 h-4 w-px bg-border" />

        {/* Heading dropdown */}
        <ToolbarDropdown label={`Heading: ${currentHeadingLabel}`} title="Heading Level">
          {HEADING_OPTIONS.map((heading) => (
            <button
              key={heading.level}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (heading.level === 0) {
                  editor.chain().focus().setParagraph().run()
                } else {
                  editor
                    .chain()
                    .focus()
                    .toggleHeading({ level: heading.level as 1 | 2 | 3 | 4 | 5 | 6 })
                    .run()
                }
              }}
              className={cn(
                'w-full rounded px-2 py-1 text-left hover:bg-muted',
                heading.level === 0 && 'text-sm',
                heading.level === 1 && 'text-xl font-bold',
                heading.level === 2 && 'text-lg font-bold',
                heading.level === 3 && 'text-base font-bold',
                heading.level === 4 && 'text-sm font-bold',
                heading.level === 5 && 'text-xs font-bold',
                heading.level === 6 && 'text-xs font-semibold text-muted-foreground',
                currentHeadingLevel === heading.level && 'bg-muted',
              )}
            >
              {heading.label}
            </button>
          ))}
        </ToolbarDropdown>

        {/* Font size dropdown */}
        <ToolbarDropdown label={`Size: ${currentFontSizeLabel}`} title="Font Size">
          {FONT_SIZES.map((fontSize) => (
            <button
              key={fontSize.label}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (fontSize.value) {
                  editor.chain().focus().setFontSize(fontSize.value).run()
                } else {
                  editor.chain().focus().unsetFontSize().run()
                }
              }}
              className={cn(
                'w-full rounded px-2 py-1 text-left hover:bg-muted',
                currentFontSizeLabel === fontSize.label && 'bg-muted',
              )}
              style={{ fontSize: fontSize.value || undefined }}
            >
              {fontSize.label}
            </button>
          ))}
        </ToolbarDropdown>

        <span className="mx-1 h-4 w-px bg-border" />

        {/* Font family */}
        <ToolbarDropdown label="Font" title="Font Family">
          {FONT_FAMILIES.map((font) => (
            <button
              key={font.value}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => editor.chain().focus().setFontFamily(font.value).run()}
              className={cn(
                'w-full rounded px-2 py-1 text-left text-sm hover:bg-muted',
                editor.isActive('textStyle', { fontFamily: font.value }) && 'bg-muted',
              )}
              style={{ fontFamily: font.value }}
            >
              {font.label}
            </button>
          ))}
        </ToolbarDropdown>

        <span className="mx-1 h-4 w-px bg-border" />

        {/* Text color */}
        <Popover>
          <PopoverTrigger asChild>
            <span className="group/tooltip relative inline-flex">
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                className="rounded p-1 transition-colors hover:bg-muted"
              >
                <Palette className={iconSize} />
              </button>
              <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-primary px-2 py-1 text-xs text-primary-foreground opacity-0 transition-opacity group-hover/tooltip:opacity-100">
                Text Color
              </span>
            </span>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <ColorPickerGrid
              activeColor={editor.getAttributes('textStyle').color as string | undefined}
              onSelect={(color) => editor.chain().focus().setColor(color).run()}
              onClear={() => editor.chain().focus().unsetColor().run()}
            />
          </PopoverContent>
        </Popover>

        {/* Highlight color */}
        <Popover>
          <PopoverTrigger asChild>
            <span className="group/tooltip relative inline-flex">
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                className="rounded p-1 transition-colors hover:bg-muted"
              >
                <Highlighter className={iconSize} />
              </button>
              <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-primary px-2 py-1 text-xs text-primary-foreground opacity-0 transition-opacity group-hover/tooltip:opacity-100">
                Highlight
              </span>
            </span>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <ColorPickerGrid
              activeColor={editor.getAttributes('highlight').color as string | undefined}
              onSelect={(color) => editor.chain().focus().toggleHighlight({ color }).run()}
              onClear={() => editor.chain().focus().unsetHighlight().run()}
            />
          </PopoverContent>
        </Popover>

        <span className="mx-1 h-4 w-px bg-border" />

        {/* Link */}
        <LinkPopover editor={editor} iconSize={iconSize} />

        {/* Image */}
        <ToolbarButton
          onClick={() => {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = 'image/jpeg,image/png,image/gif,image/webp,image/tiff'
            input.onchange = () => {
              const file = input.files?.[0]
              if (!file) return
              handleImageUpload(file)
                .then((url) => {
                  editor.chain().focus().setImage({ src: url }).run()
                })
                .catch(() => {})
            }
            input.click()
          }}
          title="Insert Image"
          disabled={uploading}
        >
          {uploading ? (
            <Loader2 className={cn(iconSize, 'animate-spin')} />
          ) : (
            <ImageIcon className={iconSize} />
          )}
        </ToolbarButton>

        <span className="mx-1 h-4 w-px bg-border" />

        {/* Clear formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          title="Clear Formatting"
        >
          <RemoveFormatting className={iconSize} />
        </ToolbarButton>

        <span className="mx-1 h-4 w-px bg-border" />

        {/* Undo/Redo */}
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo">
          <Undo className={iconSize} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo">
          <Redo className={iconSize} />
        </ToolbarButton>
      </div>

      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none px-3 py-2 min-h-[80px] focus-within:outline-none [&_.tiptap]:outline-none [&_.tiptap_p.is-editor-empty:first-child::before]:text-muted-foreground [&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.tiptap_p.is-editor-empty:first-child::before]:float-left [&_.tiptap_p.is-editor-empty:first-child::before]:h-0 [&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none [&_.tiptap_img]:max-w-full [&_.tiptap_img]:rounded [&_.tiptap_img]:my-2 [&_.tiptap_img.ProseMirror-selectednode]:outline [&_.tiptap_img.ProseMirror-selectednode]:outline-2 [&_.tiptap_img.ProseMirror-selectednode]:outline-primary [&_.tiptap_img.ProseMirror-selectednode]:rounded"
      />

      {uploading && (
        <div className="flex items-center gap-2 border-t px-3 py-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t('shared.richTextEditor.uploading')}
        </div>
      )}

      {uploadError && (
        <div className="flex items-center gap-2 border-t px-3 py-1.5 text-xs text-destructive">
          {uploadError}
        </div>
      )}
    </div>
  )
}
